import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CertificatePopUp from 'src/components/Discover/DiscoverPage/PopUps/CertificatePopUp/CertificatePopUp';
import LinkedInPopUp from 'src/components/Discover/DiscoverPage/PopUps/LinkedInPopUp/LinkedInPopUp';
import RangePopUp from 'src/components/Discover/DiscoverPage/PopUps/RangePopUp/RangePopUp';
import RatingPopUp from 'src/components/Discover/DiscoverPage/PopUps/RatingPopUp/RatingPopUp';
import HeartPopUp from 'src/components/ui/Features/HeartPopUp/HeartPopUp';
import { HeartType } from 'src/model/enum/enums';
import { popUpActions, PopUpConfig } from 'src/store/slices/popUpSlice';
import { RootState } from 'src/store/store';
import { useLearnWizard } from '../components/useLearnWizard';
import useProgress from '../data/useProgress';
import useUser from '../data/useUser';
import { useComponentModal } from './useComponentModal';

const usePopUp = () => {
    const dispatch = useDispatch();
    const ac = bindActionCreators(popUpActions, dispatch);
    const { isOpen, title, message, primaryButtonText,
        primaryButtonClick, secondaryButtonText,
        secondaryButtonClick, showSecondaryButton,
        showCloseButton, children, width, onClose } = useSelector((state: RootState) => state.popUp);
    const navigate = useNavigate();
    const { saveProgress } = useProgress();
    const { updateUserInfo } = useUser();
    const { close: closeComponentModal } = useComponentModal();
    const { close: closeLearnWizard } = useLearnWizard();

    const show = useCallback((popUpConfig: PopUpConfig) => {
        ac.showPopUp(popUpConfig);
    }, [ac]);

    const hide = useCallback(() => {
        ac.hidePopUp();
    }, [ac]);

    const showProPopUp = useCallback((title: string, message: string) => {
        const config = {
            title: title,
            message: message,
            primaryButtonText: 'See Plans',
            primaryButtonClick: () => {
                closeComponentModal();
                closeLearnWizard();
                navigate('/membership');
            },
            secondaryButtonText: 'Later',
            showSecondaryButton: true,
            showCloseButton: true,
        };

        ac.showPopUp(config);
    }, [ac, navigate, closeComponentModal, closeLearnWizard]);

    const showHeartPopUp = useCallback((heartType: HeartType) => {
        const config = {
            children: React.createElement(HeartPopUp, { heartType }),
            width: '85%',
        };
        ac.showPopUp(config);
    }, [ac]);

    const showAdsPopUp = useCallback(() => {
        const config = {
            title: 'WordUp Basic',
            message: 'You are still on Basic. To continue using WordUp, please select:',
            primaryButtonText: 'Pro Options',
            primaryButtonClick: () => {
                navigate('/membership');
            },
            secondaryButtonText: 'Whatch an Ad',
            secondaryButtonClick: () => {
                navigate('/show-ads');
            },
            onClose: () => {
                navigate('/show-ads');
            },
            showSecondaryButton: true,
            showCloseButton: true,
        };

        ac.showPopUp(config);
    }, [ac, navigate]);

    const showCertificatePopUp = useCallback(() => {
        const config = {
            children: React.createElement(CertificatePopUp),
            onClose: () => {
                saveProgress({
                    Action: 'Certificate',
                    Value: 'no'
                });
            },
            width: '85%',
        };
        ac.showPopUp(config);
        updateUserInfo({ ShowCertificate: false });
    }, [ac, saveProgress, updateUserInfo]);

    const showLinkedInPopUp = useCallback(() => {
        const config = {
            children: React.createElement(LinkedInPopUp),
            width: '85%',
        };
        ac.showPopUp(config);
        updateUserInfo({ ShowLinkedin: false });
    }, [ac, updateUserInfo]);

    const showRangePopUp = useCallback((isEasy: boolean) => {
        const config = {
            children: React.createElement(RangePopUp, { isEasy })
        };
        ac.showPopUp(config);
    }, [ac]);

    const showRatingPopUp = useCallback(() => {
        const config = {
            children: React.createElement(RatingPopUp)
        };
        ac.showPopUp(config);
    }, [ac]);

    const showClosePopUp = useCallback((onConfirm?: () => void) => {
        const config = {
            title: 'Sure?',
            message: 'Do you want to quit?',
            primaryButtonText: 'Confirm',
            primaryButtonClick: () => {
                onConfirm?.();
            },
            secondaryButtonText: 'Cancel',
            showSecondaryButton: true,
            showCloseButton: true,
        };

        ac.showPopUp(config);
    }, [ac]);


    return {
        isOpen,
        title,
        message,
        primaryButtonText,
        primaryButtonClick,
        secondaryButtonText,
        secondaryButtonClick,
        showSecondaryButton,
        showCloseButton,
        width,
        children,
        show,
        hide,
        showProPopUp,
        showHeartPopUp,
        showAdsPopUp,
        onClose,
        showCertificatePopUp,
        showLinkedInPopUp,
        showRangePopUp,
        showRatingPopUp,
        showClosePopUp
    };
};

export default usePopUp;
